import { useCallback, useRef, useState } from 'react'
import { useHistoryCustomBack } from 'weblite-router'
// Material
import { Box } from '@mui/material'
import { LIMITS } from '@weblite/permission'
// Common
import { PageWithAppBar } from '$common/page-with-app-bar'
import { PhoneInput, PhoneInputRef, TextInput } from '$common/input'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { Label } from '$common/label'
// Localization
import { t } from '$setup/internationalization'
import { closeAddContact } from '$logic/router'
import { addContacts } from '$logic/contacts/service'
import { isPhoneValidByLib } from '$func/phone'
import { showAddContactsSnackbars } from '$logic/contacts/helper'

const { userConstraints } = LIMITS

export function AddContact() {
  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [phone, setPhone] = useState('')
  const [alertText, setAlertText] = useState()
  const phoneInputRef = useRef<PhoneInputRef>(null)

  const reset = useCallback(() => {
    setFirstname('')
    setLastname('')
    setPhone('')
  }, [])

  const onBack = useCallback(() => {
    reset()
    closeAddContact()
  }, [reset])
  useHistoryCustomBack(() => {
    onBack()
  }, [onBack])
  const onSubmit = useCallback(async () => {
    if (!phone) {
      setAlertText(t('phoneInput.errorMessage'))
      throw new Error('errorMessage')
    }

    const fullNumber = phoneInputRef.current?.getPhone(phone) || ''
    const isValid = isPhoneValidByLib(fullNumber)
    if (isValid) {
      const res = await addContacts([
        { firstname, lastname, phone: fullNumber },
      ])
      const addedToContacts = Boolean(res.payload?.contacts[0]?.id)
      showAddContactsSnackbars(
        { [firstname + (lastname ? ' ' + lastname : '')]: [fullNumber] },
        {
          [fullNumber]: addedToContacts ? 'addedToContacts' : 'noAppAccount',
        },
      )
      onBack()
    } else setAlertText(t('signin.serverCodes.INVALID_PHONE'))
  }, [firstname, lastname, phone, onBack])

  const onPhoneChange = useCallback((phoneNumber: string) => {
    setAlertText(undefined)
    setPhone(phoneNumber)
  }, [])

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          onBack={onBack}
          title={t('contacts.add')}
          onConfirm={onSubmit}
          confirmDisabled={!firstname || !phone}
        />
      }
    >
      <Box
        sx={{
          p: '20px 20px 0',
          bgcolor: 'white',
          boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.05)',
          height: '346px',
          boxSizing: 'border-box',
        }}
      >
        <Label title={t('user.name')} required />
        <TextInput
          placeholder={t('user.enterName')}
          value={firstname}
          onChange={setFirstname}
          maxLength={userConstraints.firstname.maxLength}
        />

        <Label title={t('user.lastName')} />
        <TextInput
          placeholder={t('user.enterLastName')}
          value={lastname}
          onChange={setLastname}
          maxLength={userConstraints.lastname.maxLength}
        />

        <Label title={t('user.phone')} required />
        <PhoneInput
          value={phone}
          onChange={onPhoneChange}
          errorMessage={alertText}
          hasError={!!alertText}
          ref={phoneInputRef}
        />
      </Box>
    </PageWithAppBar>
  )
}
